/**
 * SimpleSelectBox.tsx
 * This select box loads the data from a GET endpoint and displays the options in a select box.
 * The user can select one option from the list.
 * The api needs to return an array of objects with the properties `name` and `id`.
 */
import React, { HtmlHTMLAttributes } from 'react'
import useSWRImmutable from 'swr/immutable'

import { simpleFetch } from '../../../api'
import type { University } from '../../../api/universities/types'
import Loading from '../../components/layout/Loading'
import SelectInput from '../../components/form/SelectInput'

interface Props extends HtmlHTMLAttributes<Partial<HTMLSelectElement>> {
  value: string | undefined
  name: string
  apiUrl: string
  label: string
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  required?: boolean
  alwaysVisibleLabels?: string[]
}
const SimpleSelectBox: React.FC<Props> = ({ handleChange, value, name, apiUrl, label, alwaysVisibleLabels, ...rest }) => {
  const { data, isLoading } = useSWRImmutable(apiUrl, simpleFetch<University>)

  if(!data || isLoading) return <Loading />

  const onChangeHandler = (value: string): void => {
    const event: React.ChangeEvent<HTMLInputElement> = { target: { name, value } } as React.ChangeEvent<HTMLInputElement>
    handleChange(event)
  }

  const options: Array<{ label: string, value: string }> = data.map(({ name, id }) => ({ label: name, value: id }))
  if(!options) return null
  return (
    <SelectInput
      name={name}
      isDisabled={options?.length === 0}
      options={options}
      isMulti={false}
      label={label}
      value={value ? { value, label: options.find(option => option.value.toString() === value)?.label ?? '' } : undefined}
      {...rest}
      onChange={onChangeHandler}
      alwaysVisibleLabels={alwaysVisibleLabels}
    />
  )
}

export default SimpleSelectBox
